<div class="jobsite-gallery-section" (click)="onDocumentClick($event)"
  (dragenter)="onDragEnter($event)"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onFileDrop($event)">

  <!-- DRAG & DROP OVERLAY — a direct child of the positioned root so it covers
       the whole widget, matching module-documents. Nested inside the media grid
       it only shaded that region. -->
  @if (isDragging && canDropFiles()) {
    <div class="drag-overlay" @slideInFromTop role="region" aria-label="Drag and drop upload area">
      <div class="upload-indicator">
        @if (draggedFileCount > 0) {
          <div class="file-count-badge">
            {{ draggedFileCount }} file{{ draggedFileCount > 1 ? 's' : '' }}
          </div>
        }
        <div class="upload-icon-circle">
          <i class="fas fa-cloud-upload-alt upload-icon"></i>
        </div>
        <h3 class="upload-title">Drop files to upload</h3>
        <p class="upload-subtitle">Release to upload {{ draggedFileCount }} file(s)</p>
      </div>
    </div>
  }

  <ng-container>
    <div class="card-header">
      <div class="header-title">
        <mat-icon>photo_library</mat-icon>
        <h3>{{ galleryTitle }}</h3>
        <p>{{ mediaCount }} items @if (selectedCount > 0) {
          <span>• {{ selectedCount }} selected</span>
        }</p>
      </div>
      <div class="header-actions">
        <button mat-icon-button class="view-toggle-button" (click)="toggleViewMode()" matTooltip="Toggle view">
          <mat-icon>{{ viewMode === 'grid' ? 'view_list' : 'view_module' }}</mat-icon>
        </button>
        <button mat-button class="upload-button" (click)="openUploadDialog()" [disabled]="isLocked || !canUpload()" [appPermission]="uploadPermission" [ngClass]="{'permission-disabled': isLocked || !canUpload()}">
          <mat-icon>upload</mat-icon>
          Upload
        </button>
      </div>
    </div>

    <!-- Breadcrumb Navigation -->
    @if (breadcrumbs.length > 0) {
      <div class="breadcrumb-navigation">
        <div class="breadcrumb-container">
          <button class="breadcrumb-item" (click)="navigateToHome()">
            <mat-icon class="breadcrumb-icon">home</mat-icon>
            <span>All Media</span>
          </button>
          <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
          @for (breadcrumb of breadcrumbs; track breadcrumb; let i = $index; let last = $last) {
            <button class="breadcrumb-item"
              [class.current]="last"
              (click)="!last && navigateToBreadcrumb(i)">
              <mat-icon class="breadcrumb-icon">folder</mat-icon>
              <span>{{ breadcrumb.name }}</span>
            </button>
            @if (!last) {
              <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
            }
          }
        </div>
      </div>
    }

    <!-- Enhanced Controls Section with Folder Navigation -->
    <div class="controls-section">
      <div class="controls-wrapper">
        <!-- Folder Navigation -->
        <div class="folder-navigation">
          <!-- Desktop: Horizontal Tabs with Scrolling -->
          @if (!isMobileView) {
            <div class="folder-tabs-container">
              <!-- Left scroll button -->
              <button mat-icon-button
                class="folder-scroll-btn folder-scroll-left"
                [class.disabled]="!canScrollLeft()"
                [disabled]="!canScrollLeft()"
                (click)="scrollFoldersLeft()"
                matTooltip="Previous folders">
                <mat-icon>chevron_left</mat-icon>
              </button>
              <!-- Folder tabs viewport -->
              <div class="folder-tabs-viewport">
                <div class="folder-tabs" [style.transform]="'translateX(0)'">
                  @for (folder of visibleFolders; track folder) {
                    <button class="folder-tab"
                      [class.active]="currentFolder === folder.id"
                      (click)="selectFolder(folder.id)">
                      <mat-icon class="folder-icon">{{ folder.id === 0 ? 'photo_library' : 'folder' }}</mat-icon>
                      <span class="folder-name">{{ folder.name }}</span>
                      <span class="folder-count">{{ folder.count }}</span>
                    </button>
                  }
                </div>
              </div>
              <!-- Right scroll button -->
              <button mat-icon-button
                class="folder-scroll-btn folder-scroll-right"
                [class.disabled]="!canScrollRight()"
                [disabled]="!canScrollRight()"
                (click)="scrollFoldersRight()"
                matTooltip="More folders">
                <mat-icon>chevron_right</mat-icon>
              </button>
              <!-- Create folder button -->
              <button mat-icon-button class="create-folder-btn" (click)="createFolder()" matTooltip="Create folder" [disabled]="isLocked" [ngClass]="{'permission-disabled': isLocked}">
                <mat-icon>create_new_folder</mat-icon>
              </button>
            </div>
          }

          <!-- Mobile: Dropdown -->
          @if (isMobileView) {
            <div class="folder-dropdown">
              <button class="folder-select-trigger" (click)="toggleFolderDropdown()">
                <mat-icon>folder</mat-icon>
                <span class="current-folder">{{ getFolderName(currentFolder) }}</span>
                <span class="folder-count">{{ getFolderCount(currentFolder) }}</span>
                <mat-icon class="dropdown-arrow">{{ showFolderDropdown ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>
              @if (showFolderDropdown) {
                <div class="folder-dropdown-menu">
                  @for (folder of folders; track folder) {
                    <button class="dropdown-item"
                      [class.active]="currentFolder === folder.id"
                      (click)="selectFolder(folder.id); showFolderDropdown = false">
                      <mat-icon>{{ folder.id === 0 ? 'photo_library' : 'folder' }}</mat-icon>
                      <span>{{ folder.name }}</span>
                      <span class="count">{{ folder.count }}</span>
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>

        <!-- Existing Controls -->
        <div class="search-and-filters">
          <!-- Back Button (only shown when inside subfolders) -->
          @if (shouldShowBackButton()) {
            <button mat-icon-button
              class="back-button"
              (click)="goBackToPreviousFolder()"
              matTooltip="Go back">
              <mat-icon>arrow_back</mat-icon>
            </button>
          }

          <!-- Search Container -->
          <div class="search-container">
            <mat-icon>search</mat-icon>
            <input type="text"
              class="search-input"
              placeholder="Search media..."
              [(ngModel)]="searchTerm"
              (input)="applyFilters()">
          </div>

          <!-- Warehouse-only: filter by source delivery -->
          @if (enableDeliveryFilter && deliveryFilterOptions.length) {
            <div class="delivery-filter">
              <mat-icon>local_shipping</mat-icon>
              <select class="delivery-filter-select"
                [(ngModel)]="selectedDeliveryCode"
                (change)="applyFilters()">
                <option value="">All deliveries</option>
                @for (code of deliveryFilterOptions; track code) {
                  <option [value]="code">{{ code }}</option>
                }
              </select>
            </div>
          }

          <!-- Filter Buttons -->
          <div class="filter-buttons">
            <button class="filter-button"
              [class.active]="filter === 'all'"
              (click)="setFilter('all')">
              All
            </button>
            <button class="filter-button"
              [class.active]="filter === 'photos'"
              (click)="setFilter('photos')">
              <mat-icon class="button-icon">image</mat-icon>
              Photos
            </button>
            <button class="filter-button"
              [class.active]="filter === 'videos'"
              (click)="setFilter('videos')">
              <mat-icon class="button-icon">videocam</mat-icon>
              Videos
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Legacy controls removed -->
    <!--<div class="gallery-controls">
  </div>-->

  @if (selectedCount > 0 || canPaste()) {
    <div class="gallery-toolbar">
      @if (selectedCount > 0) {
        <div class="selected-info">
          {{ selectedCount }} item(s) selected
        </div>
      }
      <div class="bulk-actions">
        <button mat-button
          (click)="bulkDownload()"
          [appPermission]="downloadPermission"
          [disabled]="selectedCount !== 1 || !canDownload()"
          [ngClass]="{'permission-disabled': !canDownload()}"
          matTooltip="Download selected item (single item only)">
          <mat-icon>download</mat-icon>
          Download
        </button>
        <button mat-button
          (click)="bulkCopy()"
          [appPermission]="copyPermission"
          [disabled]="!hasItemsSelected() || isLocked || !canCopy()"
          [ngClass]="{'permission-disabled': isLocked || !canCopy()}"
          matTooltip="Copy selected item(s)">
          <mat-icon>content_copy</mat-icon>
          Copy
        </button>
        <button mat-button
          (click)="bulkMove()"
          [appPermission]="cutPermission"
          [disabled]="!hasItemsSelected() || isLocked || !canCut()"
          [ngClass]="{'permission-disabled': isLocked || !canCut()}"
          matTooltip="Cut selected item(s) for moving">
          <mat-icon>content_cut</mat-icon>
          Cut
        </button>
        <button mat-button
          (click)="bulkPaste()"
          [disabled]="!canPaste() || isLocked"
          [ngClass]="{'permission-disabled': isLocked}"
          [class.highlight]="canPaste()"
          matTooltip="Paste copied/cut item(s) to current folder">
          <mat-icon>content_paste</mat-icon>
          Paste
          @if (canPaste()) {
            <span class="paste-info">({{ copiedItemType === 'copy' ? 'Copy' : 'Cut' }}: {{ getClipboardDisplayText() }})</span>
          }
        </button>
        <button mat-button
          class="delete-button"
          (click)="bulkDelete()"
          [appPermission]="deletePermission"
          [disabled]="!hasItemsSelected() || isLocked || !canDelete()"
          [ngClass]="{'permission-disabled': isLocked || !canDelete()}"
          matTooltip="Delete selected item(s)">
          <mat-icon>delete</mat-icon>
          Delete
        </button>
      </div>
      <button mat-icon-button
        class="close-toolbar-btn"
        (click)="closeToolbar()"
        matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  }

  <!-- UPLOAD PROGRESS PANEL (ported from Task Attachments) -->
  @if (uploadQueue.length > 0) {
    <div class="upload-progress-panel" @slideInFromTop>
      <div class="upload-panel-header">
        <div class="header-info">
          <i class="fas fa-upload"></i>
          <span class="upload-count">
            Uploading {{ getUploadingCount() }} of {{ uploadQueue.length }} files
          </span>
        </div>
        <div class="header-actions">
          <button type="button" class="btn-icon" (click)="toggleUploadPanel()">
            <i class="fas" [ngClass]="isUploadPanelOpen ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </button>
          @if (hasCompletedUploads()) {
            <button type="button" class="btn-icon" (click)="clearCompleted()">
              <i class="fas fa-check"></i>
            </button>
          }
          <button type="button" class="btn-icon" (click)="clearAll()">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
      @if (isUploadPanelOpen) {
        <div class="upload-file-list">
          @for (file of uploadProgressList; track file.id) {
            <div class="upload-file-item">
              <div class="upload-file-preview">
                @if (file.preview) {
                  <img [src]="file.preview" [alt]="file.fileName">
                } @else {
                  <i class="fas fa-photo-video upload-file-icon"></i>
                }
              </div>
              <div class="upload-file-info">
                <div class="upload-file-name" [title]="file.fileName">{{ file.fileName }}</div>
                <div class="upload-file-details">
                  <span class="upload-file-size">{{ file.fileSize }}</span>
                  @switch (file.status) {
                    @case ('pending') {
                      <span class="upload-file-status"><i class="fas fa-clock"></i>Waiting...</span>
                    }
                    @case ('uploading') {
                      <span class="upload-file-status">
                        <i class="fas fa-spinner fa-spin"></i>
                        {{ file.progress }}% • {{ formatUploadSpeed(file.uploadSpeed || 0) }}
                        @if (file.timeRemaining) {
                          <span> • {{ formatTimeRemaining(file.timeRemaining) }}</span>
                        }
                      </span>
                    }
                    @case ('complete') {
                      <span class="upload-file-status upload-status-success">
                        <i class="fas fa-check-circle"></i>Complete
                      </span>
                    }
                    @case ('error') {
                      <span class="upload-file-status upload-status-error">
                        <i class="fas fa-exclamation-circle"></i>{{ file.error || 'Failed' }}
                      </span>
                    }
                    @case ('cancelled') {
                      <span class="upload-file-status upload-status-cancelled">
                        <i class="fas fa-ban"></i>Cancelled
                      </span>
                    }
                  }
                </div>
                @if (file.status === 'uploading') {
                  <div class="upload-file-progress">
                    <div class="progress">
                      <div class="progress-bar" [style.width.%]="file.progress"></div>
                    </div>
                  </div>
                }
              </div>
              <div class="upload-file-actions">
                @if (file.status === 'uploading') {
                  <button class="btn-icon btn-icon-danger" (click)="cancelUpload(file)">
                    <i class="fas fa-times"></i>
                  </button>
                }
                @if (file.status === 'error') {
                  <button class="btn-icon btn-icon-primary" (click)="retryUpload(file)">
                    <i class="fas fa-redo"></i>
                  </button>
                }
                @if (file.status === 'complete' || file.status === 'error' || file.status === 'cancelled') {
                  <button class="btn-icon" (click)="removeFromQueue(file)">
                    <i class="fas fa-trash"></i>
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- Gallery Container (Full Width) -->
  <div class="gallery-container">
    <div class="gallery-content-fullwidth" [style.height]="maxContentHeight || null" [style.max-height]="maxContentHeight || null" [style.overflow-y]="maxContentHeight ? 'auto' : null">
      <!-- Initial 5s warmup: queue job hasn't had time to run yet -->
      @if (isInitialPreparing) {
        <div class="loading-state">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Preparing...</p>
        </div>
      }

      <!-- Post-404 state: folders still being created by the queue job -->
      @if (isFoldersPreparingState && !isInitialPreparing) {
        <div class="loading-state">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Gallery folders are being prepared...</p>
        </div>
      }

      <!-- Loading state -->
      @if (isLoading && !isInitialPreparing && !isFoldersPreparingState) {
        <div class="loading-state">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Loading media...</p>
        </div>
      }

      <!-- Error state -->
      @if (hasError && !isLoading && !isInitialPreparing && !isFoldersPreparingState) {
        <div class="error-state">
          <mat-icon>error</mat-icon>
          <p>{{ errorMessage }}</p>
          <button mat-button (click)="loadMediaItems()">Retry</button>
        </div>
      }

      <!-- Empty state -->
      @if (!isLoading && !hasError && !isInitialPreparing && !isFoldersPreparingState && filteredMedia.length === 0) {
        <div class="empty-state">
          <div class="empty-icon-wrapper">
            <mat-icon class="empty-icon">photo_library</mat-icon>
          </div>
          <h3 class="empty-title">No media found</h3>
          <p class="empty-description">
            {{ searchTerm ? 'No media matches your search' : 'Upload photos and videos to get started' }}
          </p>
          <div class="empty-actions">
            @if (searchTerm) {
              <button mat-button class="reset-button" (click)="searchTerm = ''; applyFilters()">
                Clear Search
              </button>
            }
            <button mat-button class="upload-button-empty" (click)="openUploadDialog()" [disabled]="isLocked || !canUpload()" [appPermission]="uploadPermission" [ngClass]="{'permission-disabled': isLocked || !canUpload()}">
              <mat-icon class="button-icon">upload</mat-icon>
              Upload Media
            </button>
          </div>
        </div>
      }

      <!-- Grid View -->
      @if (viewMode === 'grid' && !isLoading && !hasError && !isInitialPreparing && !isFoldersPreparingState && filteredMedia.length > 0) {
        <div class="gallery-grid">
          @for (item of filteredMedia; track item) {
            <div class="gallery-item"
              [class.selected]="item.selected"
              [class.folder-item]="item.type === 'folder'"
              (click)="item.type === 'folder' ? navigateToFolder(item) : preview(item)"
              (contextmenu)="onContextMenu($event, item)">
              <div class="item-checkbox">
                <mat-checkbox class="bidone-checkbox" [(ngModel)]="item.selected"
                  (click)="$event.stopPropagation()"
                (change)="updateSelectedCount()"></mat-checkbox>
              </div>
              @if (item.is_primary) {
                <div class="item-badge">
                  <mat-icon>star</mat-icon>
                </div>
              }
              <div class="item-thumbnail">
                <!-- Folder icon -->
                @if (item.type === 'folder') {
                  <div class="folder-icon-wrapper">
                    <mat-icon class="large-folder-icon">folder</mat-icon>
                  </div>
                }
                <!-- Image thumbnail -->
                @if (item.type !== 'folder' && isImage(item)) {
                  <img
                    [src]="item.thumbnail_url"
                    [alt]="item.name"
                    loading="lazy">
                }
                <!-- Video thumbnail -->
                @if (item.type !== 'folder' && isVideo(item)) {
                  <div class="video-thumbnail">
                    <video [src]="item.url"></video>
                    <div class="video-play-overlay">
                      <mat-icon>play_circle_filled</mat-icon>
                    </div>
                  </div>
                }
                <!-- PDF and other file types -->
                @if (item.type !== 'folder' && !isImage(item) && !isVideo(item)) {
                  <div class="file-icon-wrapper">
                    <mat-icon class="large-file-icon">
                      {{ item.file_type === 'pdf' ? 'picture_as_pdf' : 'insert_drive_file' }}
                    </mat-icon>
                    <span class="file-extension">{{ item.file_type?.toUpperCase() }}</span>
                  </div>
                }
                <!-- Avatar moved to thumbnail overlay (only for files, not folders) -->
                @if (item.type !== 'folder') {
                  <div class="thumbnail-avatar"
                    [title]="item.uploaded_by"
                    [style.background]="!item.user_avatar ? getAvatarColor(item.uploaded_by || '') : ''">
                    @if (item.user_avatar) {
                      <img
                        [src]="item.user_avatar"
                        [alt]="item.uploaded_by"
                        (error)="item.user_avatar = undefined">
                    }
                    @if (!item.user_avatar) {
                      <span class="avatar-initials"
                        >
                        {{ item.user_initials || 'U' }}
                      </span>
                    }
                  </div>
                }
                @if (item.type !== 'folder') {
                  <div class="item-overlay">
                    <button mat-icon-button (click)="preview(item); $event.stopPropagation()" matTooltip="Preview">
                      <mat-icon>visibility</mat-icon>
                    </button>
                    <button mat-icon-button (click)="editItem(item); $event.stopPropagation()" matTooltip="Edit">
                      <mat-icon>edit</mat-icon>
                    </button>
                    <button mat-icon-button (click)="download(item); $event.stopPropagation()" matTooltip="Download" [appPermission]="downloadPermission" [disabled]="!canDownload()" [ngClass]="{'permission-disabled': !canDownload()}">
                      <mat-icon>download</mat-icon>
                    </button>
                  </div>
                }
              </div>
              <div class="item-info">
                <span class="item-name"
                  [contentEditable]="item.editing"
                  (blur)="saveItemName(item, $event)"
                (click)="$event.stopPropagation()">{{ item.name }}</span>
                <span class="item-meta">
                  {{ item.size | number }} KB • {{ item.date | date:'short' }}
                </span>
              </div>
            </div>
          }
        </div>
      }

      <!-- List View -->
      @if (viewMode === 'list' && !isLoading && !hasError && !isInitialPreparing && !isFoldersPreparingState && filteredMedia.length > 0) {
        <div class="gallery-list">
          <table mat-table [dataSource]="filteredMedia" class="media-table">
            <!-- Checkbox Column -->
            <ng-container matColumnDef="select">
              <th mat-header-cell *matHeaderCellDef width="50">
                <mat-checkbox class="bidone-checkbox" (change)="toggleAllSelection($event)"
                  [checked]="selectedCount === filteredMedia.length && selectedCount > 0"
                  [indeterminate]="selectedCount > 0 && selectedCount < filteredMedia.length">
                </mat-checkbox>
              </th>
              <td mat-cell *matCellDef="let item">
                <mat-checkbox class="bidone-checkbox" [(ngModel)]="item.selected"
                  (change)="updateSelectedCount()">
                </mat-checkbox>
              </td>
            </ng-container>
            <!-- Thumbnail Column -->
            <ng-container matColumnDef="thumbnail">
              <th mat-header-cell *matHeaderCellDef width="60">Preview</th>
              <td mat-cell *matCellDef="let item" class="thumbnail-cell">
                @if (item.type === 'folder') {
                  <mat-icon>folder</mat-icon>
                }
                @if (item.type !== 'folder' && isImage(item)) {
                  <img [src]="item.thumbnail_url" [alt]="item.name" class="list-thumbnail">
                }
                @if (item.type !== 'folder' && isVideo(item)) {
                  <mat-icon>videocam</mat-icon>
                }
                @if (item.type !== 'folder' && !isImage(item) && !isVideo(item)) {
                  <mat-icon>
                    {{ item.file_type === 'pdf' ? 'picture_as_pdf' : 'insert_drive_file' }}
                  </mat-icon>
                }
              </td>
            </ng-container>
            <!-- Name Column -->
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Name</th>
              <td mat-cell *matCellDef="let item">
                <span class="item-name-list">{{ item.name }}</span>
                @if (item.is_primary) {
                  <mat-icon class="primary-icon">star</mat-icon>
                }
              </td>
            </ng-container>
            <!-- Type Column -->
            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef width="100">Type</th>
              <td mat-cell *matCellDef="let item">{{ item.file_type | uppercase }}</td>
            </ng-container>
            <!-- Size Column -->
            <ng-container matColumnDef="size">
              <th mat-header-cell *matHeaderCellDef width="100">Size</th>
              <td mat-cell *matCellDef="let item">{{ item.size | number }} KB</td>
            </ng-container>
            <!-- Date Column -->
            <ng-container matColumnDef="date">
              <th mat-header-cell *matHeaderCellDef width="150">Date</th>
              <td mat-cell *matCellDef="let item">{{ item.date | date:'short' }}</td>
            </ng-container>
            <!-- Actions Column -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef width="120">Actions</th>
              <td mat-cell *matCellDef="let item" class="actions-cell">
                <button mat-icon-button (click)="preview(item)" matTooltip="Preview">
                  <mat-icon>visibility</mat-icon>
                </button>
                <button mat-icon-button (click)="download(item)" matTooltip="Download" [appPermission]="downloadPermission" [disabled]="!canDownload()" [ngClass]="{'permission-disabled': !canDownload()}">
                  <mat-icon>download</mat-icon>
                </button>
                <button mat-icon-button (click)="deleteItem(item)" matTooltip="Delete" [disabled]="isLocked || !canDelete()" [appPermission]="deletePermission" [ngClass]="{'permission-disabled': isLocked || !canDelete()}">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="['select', 'thumbnail', 'name', 'type', 'size', 'date', 'actions']"></tr>
            <tr mat-row *matRowDef="let row; columns: ['select', 'thumbnail', 'name', 'type', 'size', 'date', 'actions']"
              [class.selected]="row.selected"
              (click)="row.type === 'folder' ? navigateToFolder(row) : preview(row)"
            style="cursor: pointer"></tr>
          </table>
        </div>
      }
    </div>
  </div>

  <!-- Upload progress -->
  @if (isUploading) {
    <div class="upload-progress">
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      <span>Uploading files...</span>
    </div>
  }
</ng-container>
</div>

<!-- Context Menu -->
@if (showContextMenu) {
  <div class="context-menu"
    [style.left.px]="contextMenuPosition.x"
    [style.top.px]="contextMenuPosition.y"
    (click)="$event.stopPropagation()">
    <div class="context-menu-content">
      <button class="context-menu-item" (click)="preview(contextMenuItem!); showContextMenu = false">
        <mat-icon>visibility</mat-icon>
        Preview
      </button>
      <button class="context-menu-item" (click)="editItem(contextMenuItem!); showContextMenu = false">
        <mat-icon>edit</mat-icon>
        Edit Details
      </button>
      <button class="context-menu-item" (click)="download(contextMenuItem!); showContextMenu = false" [appPermission]="downloadPermission" [disabled]="!canDownload()" [ngClass]="{'permission-disabled': !canDownload()}">
        <mat-icon>download</mat-icon>
        Download
      </button>
      <mat-divider></mat-divider>
      <button class="context-menu-item" (click)="setAsPrimary(contextMenuItem!); showContextMenu = false">
        <mat-icon>star</mat-icon>
        Set as Primary
      </button>
      <button class="context-menu-item" (click)="moveToFolder(contextMenuItem!); showContextMenu = false">
        <mat-icon>drive_file_move</mat-icon>
        Move to Folder
      </button>
      <mat-divider></mat-divider>
      <button class="context-menu-item delete" (click)="deleteItem(contextMenuItem!); showContextMenu = false" [disabled]="isLocked || !canDelete()" [appPermission]="deletePermission" [ngClass]="{'permission-disabled': isLocked || !canDelete()}">
        <mat-icon>delete</mat-icon>
        Delete
      </button>
    </div>
  </div>
}